import { ReactNode, useCallback, useId, useState } from "react";

import {
  FilterItemOptionValue,
  HierarchyFilterItemMapOption,
  HierarchyFilterOptionsMap,
} from "components/Filters/types";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Box from "ds/components/Box";
import Tooltip from "ds/components/Tooltip";
import Tree, {
  TreeBranch,
  TreeBranchLink,
  TreeItem,
  TreeItemContent,
  TreeItemToggle,
} from "ds/components/Tree";

import { getSelectionState } from "./helpers";
import FilterOption from "./FilterOption";
import styles from "./styles.module.css";

type HierarchyFilterOptionProps = {
  item: HierarchyFilterItemMapOption;
  optionsMap: HierarchyFilterOptionsMap;
  handleSelectValue: (
    optionsMap?: HierarchyFilterOptionsMap
  ) => (value: string, noUnselection?: boolean, selectOnlyImmediateChildren?: boolean) => void;
  selectedValues: Set<FilterItemOptionValue>;
  hasIndentation: boolean;
  filterName: string;
};

const HierarchyFilterOption = ({
  handleSelectValue,
  item,
  optionsMap,
  selectedValues,
  hasIndentation,
  filterName,
}: HierarchyFilterOptionProps) => {
  const groupId = useId();
  const [isOpen, setIsOpen] = useState(false);

  const handleItemSelectValue = () => {
    const selectionValue = JSON.stringify({
      equals: item.id,
      includeDescendants: true,
    });

    handleSelectValue(optionsMap)(selectionValue);
  };

  const handleSelectAll = () => {
    const selectionValue = JSON.stringify({
      equals: item.id,
      includeDescendants: true,
    });

    handleSelectValue(optionsMap)(selectionValue, true);
  };

  const handleCurrentLevel = () => {
    const selectionValue = JSON.stringify({
      equals: item.id,
      includeDescendants: false,
    });

    handleSelectValue(optionsMap)(selectionValue, true, true);
  };

  const toggleSectionOpen = useCallback(() => {
    const newValue = !isOpen;
    setIsOpen(newValue);
  }, [isOpen]);

  const children = item.children.reduce<ReactNode[]>((acc, childId) => {
    const childItem = optionsMap.get(childId);

    if (childItem) {
      acc.push(
        <HierarchyFilterOption
          key={childId}
          item={childItem}
          optionsMap={optionsMap}
          handleSelectValue={handleSelectValue}
          selectedValues={selectedValues}
          filterName={filterName}
          hasIndentation={hasIndentation}
        />
      );
    }

    return acc;
  }, []);

  const itemId = item.id;

  const selectionState = getSelectionState(itemId, selectedValues, optionsMap);

  const isSelected = selectionState === "selected";
  const isIndeterminate = selectionState === "partial";

  return (
    <TreeBranch direction="column" isFirst={!item.parentId}>
      <TreeBranchLink>
        <TreeItem isIndented={children.length === 0}>
          {children.length > 0 && (
            <TreeItemToggle
              onPress={toggleSectionOpen}
              isExpanded={isOpen}
              aria-controls={groupId}
              aria-expanded={isOpen}
            />
          )}

          <TreeItemContent>
            <FilterOption
              className={styles.selectOption}
              key={item.value.toString()}
              value={item.value.toString()}
              label={item.value.toString()}
              postfix={
                <Tooltip
                  on={(props) => (
                    <span {...props}>
                      {item.directCount === item.count
                        ? `${item.directCount}`
                        : `${item.directCount} (${item.count})`}
                    </span>
                  )}
                >
                  <>{`${item.directCount} items in parent ${filterName.toLowerCase()}`}</>
                  {item.directCount !== item.count && (
                    <>
                      <br />
                      {`${item.count} items including children`}
                    </>
                  )}
                </Tooltip>
              }
              onChange={handleItemSelectValue}
              checked={isSelected}
              indeterminate={isIndeterminate}
            />

            {children.length > 0 && (
              <Box align="center" margin="0 0 0 medium">
                <DropdownMenuEllipsis
                  tooltip="Filter options"
                  placement="bottom right"
                  dotsSize="small"
                  buttonVariant="ghost"
                >
                  <DropdownMenuItem onAction={handleCurrentLevel}>
                    Select only this {`${filterName.toLowerCase()} (${item.directCount})`}
                  </DropdownMenuItem>
                  <DropdownMenuItem onAction={handleSelectAll}>
                    Select parent and all child {`${filterName.toLowerCase()}s (${item.count})`}
                  </DropdownMenuItem>
                </DropdownMenuEllipsis>
              </Box>
            )}
          </TreeItemContent>
        </TreeItem>
      </TreeBranchLink>

      {children.length > 0 && isOpen && (
        <Tree direction="column" id={groupId} role="group" aria-label={item.value.toString()}>
          {children}
        </Tree>
      )}
    </TreeBranch>
  );
};

export default HierarchyFilterOption;
